import {
  Body,
  Group,
  H5,
  Label,
  MaterialIcon,
  Radio,
  Stack,
  TextInput,
  useTheme,
} from '@qred/components-library';
import { Select } from '@qred/components-library/next';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import { CountryCode, ECardPurpose, StepStatus } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useCardPurposeOptions } from '~/hooks/useCardPurposeOptions';
import useTranslate from '~/hooks/useTranslate';
import { StepConfigItemComponentProps } from '~/Interfaces';
import { RootState } from '~/store';
import { postCardApplication } from '~/store/actions/cardApplicationActions';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { patchApplication } from '~/store/actions/loanApplicationActions';
import {
  updateApplicantData as updateApplicantDataInCardForm,
  updateForm as updateCardForm,
} from '~/store/slices/cardApplication.slice';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import useGoToNextStep from '../Steps/useGoToNextStep';
import {
  CardOptInContent,
  CardOptInPage,
  ContentCard,
} from './CardOptIn.styles';

enum CardOptInValue {
  AddCard = 'add_card',
  SkipCard = 'skip_card',
}

const OTHER_PURPOSE_REASON_TEXT_MAX_CHAR_LIMIT = 190;

export const CardOptIn = ({
  setBackgroundOverride,
}: StepConfigItemComponentProps) => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const { cardOptIn, organizationNumber, turnover, applicant } = useSelector(
    (state: RootState) => state.loanApplication.form
  );

  const hasOptedInOrNotOptedOut = cardOptIn || cardOptIn === undefined;
  const cardOptInValue = hasOptedInOrNotOptedOut
    ? CardOptInValue.AddCard
    : CardOptInValue.SkipCard;

  const loanApiStatus = useSelector(
    (state: RootState) => state.loanApplication.apiStatus
  );
  const { purposeOfCard, cardPurposeManual } = useSelector(
    (state: RootState) => state.cardApplication.form
  );
  const cardApiStatus = useSelector(
    (state: RootState) => state.cardApplication.apiStatus
  );
  const market = useSelector((state: RootState) => state.intl.market);
  const theme = useTheme();
  const goToNextStep = useGoToNextStep();
  const indicativeOfferVariant = useFeatureFlagVariantKey(
    'AB_Web_LoanApplication_IndicativeOffer_MoreSuperApplications_SE_FI'
  );
  const { cardPurposeOptions, isValidOption } = useCardPurposeOptions();

  const hasIndicativeOfferFeature = indicativeOfferVariant === 'test';

  const isQeld = market === CountryCode.NL;

  useEffect(() => {
    setBackgroundOverride?.(theme.fn.themeColor('Page/Option_2_(Green)'));

    return () => {
      setBackgroundOverride?.(undefined);
    };
  }, [setBackgroundOverride, theme]);

  const handleCardOptInChange = (
    radioButtonValue: 'add_card' | 'skip_card'
  ) => {
    const _wantsCard = radioButtonValue === 'add_card';

    dispatch(
      pushToGtmLoanAction({
        actionName: _wantsCard
          ? 'card_opt_in_option_pressed'
          : 'card_opt_out_option_pressed',
      })
    );

    dispatch(
      updateForm({
        cardOptIn: _wantsCard,
      })
    );
  };

  const onCardOptInSubmit = async () => {
    dispatch(updateForm({ cardOptIn: true }));
    dispatch(
      pushToGtmLoanAction({
        actionName: 'next_button_pressed',
        extraContext: {
          choseToApplyForCard: true,
        },
      })
    );

    dispatch(
      updateCardForm({
        organizationNumber,
        turnover,
      })
    );
    dispatch(
      updateApplicantDataInCardForm({
        email: applicant.email,
        phone: applicant.phone,
        personalNumber: applicant.personalNumber,
      })
    );
    try {
      await dispatch(
        postCardApplication({
          finalized: true,
          appliedForThroughLoanFlow: true,
        })
      );

      await dispatch(patchApplication());
    } catch (err) {
      dispatch(
        pushToGtmLoanAction({
          stepStatus: StepStatus.Error,
          errorMessage: err?.response?.data?.message,
        })
      );
    }
  };

  const onCardOptOutSubmit = () => {
    dispatch(updateForm({ cardOptIn: false }));
    goToNextStep({
      choseToApplyForCard: false,
    });
  };

  return (
    <CardOptInPage>
      <CardOptInContent>
        <Stack spacing="xl">
          <H5>{t('Onboarding.CardChoiceStepTitle')}</H5>
          <Body size="lg">{t('Onboarding.CardChoiceStepDescription')}</Body>

          <Group direction="row" spacing="sm">
            <MaterialIcon
              src="check_circle"
              color="Icon/Default"
              filled
              size={24}
            />
            <Body size="lg">
              {t('Onboarding.CardChoiceStepDescriptionUSP1')}
            </Body>
          </Group>
          <Group direction="row" spacing="sm">
            <MaterialIcon
              src="check_circle"
              color="Icon/Default"
              filled
              size={24}
            />
            <Body size="lg">
              {t('Onboarding.CardChoiceStepDescriptionUSP2')}
            </Body>
          </Group>
          <Group direction="row" spacing="sm">
            <MaterialIcon
              src="check_circle"
              color="Icon/Default"
              filled
              size={24}
            />
            <Body size="lg">
              {t('Onboarding.CardChoiceStepDescriptionUSP3')}
            </Body>
          </Group>
        </Stack>
        <Radio.Group
          direction="column"
          name="card_opt_in"
          value={cardOptInValue}
          onChange={handleCardOptInChange}
        >
          <label htmlFor="add_card">
            <ContentCard
              p="lg"
              color="Container/Option_2_(White)"
              style={{
                border:
                  cardOptInValue === CardOptInValue.AddCard
                    ? `1px solid ${theme.fn.themeColor('Border/Active')}`
                    : undefined,
              }}
            >
              <Stack spacing="lg" align="center">
                <img
                  src={isQeld ? '/qeld_card.png' : '/qred_card.webp'}
                  alt={isQeld ? 'Qeld Business Card' : 'Qred Business Card'}
                  style={{
                    width: '310px',
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />

                <Group direction="row" spacing="sm" style={{ width: '100%' }}>
                  <Radio
                    value={'add_card'}
                    id="add_card"
                    label={
                      <Label size="lg" htmlFor="add_card">
                        {t('Onboarding.CardChoiceStepOptInText')}
                      </Label>
                    }
                    labelWrapperProps={{
                      pl: 12,
                    }}
                    hint={
                      cardOptInValue === CardOptInValue.AddCard ? (
                        <Body>
                          {t('Onboarding.CardChoiceStepOptInDescription')}
                        </Body>
                      ) : undefined
                    }
                  />
                </Group>

                {cardOptInValue === CardOptInValue.AddCard && (
                  <>
                    <Select
                      label={t('CardApplication.SelectPurposeStepTitle')}
                      style={{ alignSelf: 'stretch' }}
                      data={cardPurposeOptions}
                      value={purposeOfCard}
                      onChange={(value) => {
                        if (isValidOption(value)) {
                          dispatch(
                            updateCardForm({
                              purposeOfCard: value,
                            })
                          );
                        }
                      }}
                    />

                    {purposeOfCard === ECardPurpose.OtherPurpose && (
                      <TextInput
                        label={t(
                          'CardApplication.CardPurposeOtherPurposePlaceHolder'
                        )}
                        onChange={(event) => {
                          const { value } = event.currentTarget;
                          dispatch(
                            updateCardForm({ cardPurposeManual: value })
                          );
                        }}
                        maxLength={OTHER_PURPOSE_REASON_TEXT_MAX_CHAR_LIMIT}
                        value={cardPurposeManual || ''}
                        style={{ alignSelf: 'stretch' }}
                      />
                    )}

                    <NextButton
                      size={'md'}
                      disabled={
                        !purposeOfCard ||
                        (purposeOfCard === ECardPurpose.OtherPurpose &&
                          !cardPurposeManual)
                      }
                      action={onCardOptInSubmit}
                      label={
                        hasIndicativeOfferFeature
                          ? 'Onboarding.CardChoiceStepPrimaryButtonText2'
                          : 'Onboarding.CardChoiceStepPrimaryButtonText1'
                      }
                      isLoading={
                        cardApiStatus.post === ApiStatus.Started ||
                        loanApiStatus.patch === ApiStatus.Started
                      }
                    />
                  </>
                )}
              </Stack>
            </ContentCard>
          </label>
          <label htmlFor="skip_card">
            <ContentCard p="lg" color="Container/Option_2_(White)">
              <Group direction="row" spacing="sm">
                <Radio
                  value="skip_card"
                  id="skip_card"
                  label={
                    <Label size="lg" htmlFor="skip_card">
                      {t('Onboarding.CardChoiceStepOptOutText')}
                    </Label>
                  }
                  labelWrapperProps={{
                    pl: 12,
                  }}
                />
              </Group>
            </ContentCard>
          </label>
        </Radio.Group>

        {cardOptInValue === CardOptInValue.SkipCard && (
          <NextButton
            variant="secondary"
            size={'md'}
            label={
              hasIndicativeOfferFeature
                ? 'Onboarding.CardChoiceStepSecondaryButtonText2'
                : 'Onboarding.CardChoiceStepSecondaryButtonText1'
            }
            isLoading={loanApiStatus.patch === ApiStatus.Started}
            action={onCardOptOutSubmit}
            style={{
              alignSelf: 'center',
            }}
          />
        )}
      </CardOptInContent>
    </CardOptInPage>
  );
};
